/**
 * Shared local-file → Vault upload, used by the Vault file picker and the email
 * composer's "attach from computer" / drag-drop. Runs the presigned-PUT flow:
 * init (reserve quota + presigned URL) → PUT bytes to S3 → finalize
 * (pending → uploaded). Callers should only treat the document as attachable
 * AFTER this resolves — a thrown error means nothing was attached.
 */

import { api, type VaultDocumentDto, type VaultInputPurposeDto } from '@/lib/api';

/** SHA-256 of a file as lowercase hex (required by the upload-init contract). */
export async function sha256HexOf(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export interface UploadedVaultFile {
  documentId: string;
  fileName: string;
  mimeType: string | null;
  sizeBytes: number;
  s3Key: string;
  /** The finalized document row, for callers that need the full shape. */
  document: VaultDocumentDto;
}

/**
 * Upload one local file into the Vault and return the finalized document.
 * `onProgress` is reported as a 0→1 fraction; the v1 `fetch` PUT is
 * indeterminate so it only fires 0 then 1, but the slot keeps the API
 * forward-compatible with a real progress implementation (XHR).
 */
export async function uploadLocalFileToVault(
  file: File,
  holderId: string,
  opts?: {
    onProgress?: (fraction: number) => void;
    folderId?: string | null;
    /** An arche's input, filed into its purpose's My-Fias folder. */
    purpose?: VaultInputPurposeDto;
  },
): Promise<UploadedVaultFile> {
  opts?.onProgress?.(0);
  const sha = await sha256HexOf(file);
  const init = await api.initVaultDocumentUpload({
    holderId,
    name: file.name,
    mimeType: file.type || 'application/octet-stream',
    sizeBytes: file.size,
    checksumSha256: sha,
    ...(opts?.folderId !== undefined ? { folderId: opts.folderId } : {}),
    ...(opts?.purpose ? { purpose: opts.purpose } : {}),
  });
  const put = await fetch(init.uploadUrl, {
    method: 'PUT',
    headers: init.requiredHeaders,
    body: file,
  });
  if (!put.ok) throw new Error(`Upload failed (HTTP ${put.status})`);
  const finalized = await api.finalizeVaultDocumentUpload(init.document.documentId, {
    checksumSha256: sha,
  });
  opts?.onProgress?.(1);
  const doc = finalized.document;
  return {
    documentId: doc.documentId,
    fileName: doc.name,
    mimeType: doc.mimeType,
    sizeBytes: doc.sizeBytes,
    s3Key: doc.s3Key,
    document: doc,
  };
}
